import { matchPath } from 'react-router-dom';
import { LucideIcon, ToggleRight, Settings, Tags, Layers, Palette, ClipboardCheck, QrCode, ListOrdered, SquareParking, Bell, Send, Users, ShieldCheck, User, KeyRound, ListTodo, StickyNote, FileText } from 'lucide-react';
import { BaseType } from '../types/Enums';
import { translate } from '../types/EnumTranslations';
import { AppFeature, Features } from '../api/featuresApi';

export const DEFAULT_ROUTE_TITLE = 'Molnfastigheter';

export interface MainNavDefinition {
    label: string;
    path: string;
    /** A white PNG under public/Images/Menu. */
    icon: string;
    /** react-router path patterns that mark this section as active. */
    activePatterns: string[];
    /** Only rendered for admins (filtered in useNavigationData). */
    adminOnly?: true;
    /** Hidden while this feature is off (Inställningar → Funktioner). */
    feature?: AppFeature;
    /** Sub-pages: the section opens a flyout (a drawer section on phones) instead of navigating to `path`. */
    children?: NavigationMenuItem[];
}

export const TENANTS_PATH = '/tenants';

// Hyresadmin's flyout and drawer section.
export const rentalAdminMenuItems: NavigationMenuItem[] = [
    { path: TENANTS_PATH, label: 'Hyresgäster', icon: Users },
    { path: '/leases', label: 'Hyresavtal', icon: FileText },
];

// Parkering's flyout and drawer section.
export const parkingMenuItems: NavigationMenuItem[] = [
    { path: '/parking', label: 'Parkeringsområden', icon: SquareParking },
    { path: '/parking-queue', label: 'Parkeringskö', icon: ListOrdered },
];

// The main sections, shared by the desktop sidebar and the mobile drawer.
export const mainNavDefinitions: MainNavDefinition[] = [
    { label: 'Byggnader', path: '/map', icon: '/Images/Menu/meny-byggnader.png', activePatterns: ['/', '/map'] },
    { label: 'Ekonomi', path: '/economy', icon: '/Images/Menu/meny-ekonomi.png', activePatterns: ['/economy'] },
    { label: 'Förvaltning', path: '/management', icon: '/Images/Menu/meny-underhall.png', activePatterns: ['/management'] },
    { label: 'Projekt', path: '/projects', icon: '/Images/Menu/meny-projekt.png', activePatterns: ['/projects'] },
    { label: 'Tidslinje', path: '/timeline', icon: '/Images/Menu/meny-tidslinje.png', activePatterns: ['/timeline'] },
    { label: 'Analys', path: '/analysis', icon: '/Images/Menu/meny-analys.png', activePatterns: ['/analysis'] },
    { label: 'Hyresadmin', path: '/tenants', icon: '/Images/Menu/meny-hyresgaster.png', activePatterns: ['/tenants', '/leases'], adminOnly: true, feature: 'tenants', children: rentalAdminMenuItems },
    { label: 'Parkering', path: '/parking', icon: '/Images/Menu/meny-parkering.png', activePatterns: ['/parking', '/parking-queue'], adminOnly: true, feature: 'parking', children: parkingMenuItems },
    { label: 'Nycklar', path: '/keys', icon: '/Images/Menu/meny-nycklar.png', activePatterns: ['/keys'], adminOnly: true, feature: 'keyRegister' },
];

/** Whether an item gated on `feature` shows; everything shows while the toggles load (absent = on). */
export const isFeatureVisible = (feature: AppFeature | undefined, features: Features | null) =>
    !feature || !features || features[feature];

const matchesAny = (patterns: string[], pathname: string) =>
    patterns.some((pattern) => matchPath(pattern, pathname) !== null);

export const isMainNavActive = (definition: MainNavDefinition, pathname: string) =>
    matchesAny(definition.activePatterns, pathname);

export interface NavigationMenuItem {
    path: string;
    label: string;
    icon: LucideIcon;
    /** Only rendered for superadmins. Regular admin items are gated by isAdmin at the call site. */
    superadminOnly?: boolean;
    /** Hidden while this feature is off (Inställningar → Funktioner). */
    feature?: AppFeature;
    /** Inserts a divider before this item. */
    dividerBefore?: boolean;
}

// Shared by the desktop settings flyout and the mobile drawer's Settings section.
export const settingsMenuItems: NavigationMenuItem[] = [
    { path: '/settings/general', label: 'Allmänt', icon: Settings },
    { path: '/settings/features', label: 'Funktioner', icon: ToggleRight },
    { path: '/settings/categories/location', label: 'Kategorier & taggar', icon: Tags },
    { path: '/settings/component-types', label: 'Komponenttyper & underhållsmallar', icon: Layers },
    { path: '/settings/materials', label: 'Materialbibliotek', icon: Palette },
    { path: '/settings/building-checks', label: 'Kontrollmallar', icon: ClipboardCheck },
    { path: '/settings/public-report', label: 'Publik felanmälan', icon: QrCode },
    { path: '/settings/parking-queue', label: 'Parkeringskö', icon: ListOrdered, feature: 'parking' },
    { path: '/settings/notifications', label: 'Hantera aviseringar', icon: Bell, dividerBefore: true },
    { path: '/settings/auto-assignment', label: 'Automatisk tilldelning', icon: Send },
    { path: '/settings/manage-users', label: 'Hantera användare', icon: Users },
    { path: '/superadmin', label: 'Superadmin-funktioner', icon: ShieldCheck, superadminOnly: true, dividerBefore: true },
];

// Shared by the desktop profile flyout and the mobile drawer's profile section.
// activeTodosCount is applied to the 'my-todos' item's badge at the call site.
export const profileMenuItems: NavigationMenuItem[] = [
    { path: '/profile', label: 'Profil', icon: User },
    { path: '/change-password', label: 'Ändra lösenord', icon: KeyRound },
    { path: '/my-todos', label: 'Mina att göra', icon: ListTodo },
    { path: '/my-notes', label: 'Mina anteckningar', icon: StickyNote },
];

interface RouteTitle {
    pattern: string;
    title: string;
}

// A settings item covers its whole tab, e.g. /settings/categories/location
// also stands for /settings/categories/room. Other items match themselves
// and their sub-paths.
const menuItemPattern = (path: string) =>
    path.startsWith('/settings/') ? `/${path.split('/').slice(1, 3).join('/')}/*` : `${path}/*`;

/** True when pathname is the page a menu item leads to, or a page inside it. */
export const isMenuItemActive = (item: NavigationMenuItem, pathname: string) =>
    matchPath(menuItemPattern(item.path), pathname) !== null;

/**
 * Short, static page names for the mobile top bar. Unlike browser tab titles
 * these never include building or entity names, so they stay consistent and
 * fit a narrow screen. The first matching pattern wins, so specific patterns
 * come before broad ones. Every route in App.js must resolve to a title here;
 * navigationConfig.test.ts enforces that.
 */
const routeTitles: RouteTitle[] = [
    // Building pages
    { pattern: '/building/:id', title: 'Byggnad' },
    { pattern: '/building/:id/outdoor/:category', title: translate(BaseType.Outdoor) },
    { pattern: '/building/:id/utilities/:category', title: translate(BaseType.Utility) },
    { pattern: '/building/:id/roof', title: translate(BaseType.Roof) },
    { pattern: '/building/:id/facade', title: translate(BaseType.Facade) },
    { pattern: '/building/:id/balcony', title: translate(BaseType.Balcony) },
    { pattern: '/building/:id/foundation', title: translate(BaseType.Foundation) },
    { pattern: '/building/:id/chimney', title: translate(BaseType.Chimney) },
    { pattern: '/building/:id/roof_drainage', title: translate(BaseType.RoofDrainage) },
    { pattern: '/building/:id/ground_drainage', title: translate(BaseType.GroundDrainage) },
    { pattern: '/building/:id/riser', title: translate(BaseType.Riser) },
    { pattern: '/building/:id/miscellaneous', title: translate(BaseType.Miscellaneous) },
    { pattern: '/building/:id/fixed_furnishing', title: translate(BaseType.FixedFurnishing) },
    { pattern: '/building/:id/windows', title: translate(BaseType.Window) },
    { pattern: '/building/:id/doors', title: translate(BaseType.Door) },
    { pattern: '/building/:id/sheet-metal', title: translate(BaseType.SheetMetal) },
    { pattern: '/building/:id/roof-safety', title: translate(BaseType.RoofSafety) },
    { pattern: '/building/:id/documents', title: 'Dokument & anteckningar' },
    { pattern: '/building/:id/external-contacts', title: 'Externa kontakter' },
    { pattern: '/building/:id/room/:roomId/history', title: 'Historik' },
    { pattern: '/building/:id/level/:levelId/history', title: 'Historik' },
    { pattern: '/building/:id/component/:componentId/history', title: 'Historik' },
    { pattern: '/building/:id/history', title: 'Historik' },
    { pattern: '/building/:id/room', title: 'Rum' },
    { pattern: '/building/:id/room/:roomId', title: 'Rum' },
    { pattern: '/building/:id/level', title: 'Våningsplan' },
    { pattern: '/building/:id/level/:levelId', title: 'Våningsplan' },
    // Old takplan route; App.js redirects it to the roof page
    { pattern: '/building/:id/roofplan', title: translate(BaseType.Roof) },
    { pattern: '/building/:id/component/:componentId', title: 'Komponent' },
    { pattern: '/building/:id/images', title: 'Samtliga bilder' },
    { pattern: '/building/:id/permits', title: 'Strandskydd & bygglov' },
    { pattern: '/building/:id/checks', title: 'Kontroller' },
    { pattern: '/building/:id/checks/add', title: 'Lägg till aktivitet' },
    { pattern: '/building/:id/checks/:checkKey/edit', title: 'Redigera aktivitet' },
    { pattern: '/building/:id/risks', title: 'Risker' },
    { pattern: '/building/:id/todos', title: 'Att göra' },
    { pattern: '/building/:id/operating-costs', title: 'Driftkostnader' },
    { pattern: '/building/:id/parking', title: 'Parkering' },
    { pattern: '/building/:id/parking/:areaKey', title: 'Parkering' },
    { pattern: '/building/:id/storage-units', title: 'Förråd' },
    { pattern: '/building/:id/units', title: 'Lägenheter & lokaler' },
    { pattern: '/building/:id/timeline', title: 'Tidslinje' },
    { pattern: '/building/:id/gps-map', title: 'GPS-karta' },
    { pattern: '/building/:id/projects', title: 'Projekt' },
    { pattern: '/building/:id/projects/:projectKey', title: 'Projekt' },
    { pattern: '/building/:id/validate-maintenance-components', title: 'Validera komponenter' },
    { pattern: '/building/:id/validate-maintenance-config', title: 'Validera konfiguration' },

    // Maintenance and reports
    { pattern: '/building-checks', title: 'Alla kommande aktiviteter' },
    { pattern: '/maintenance', title: 'Underhållshantering' },
    { pattern: '/maintenanceTemplates', title: 'Underhållsmallar' },
    { pattern: '/maintenanceDrafts/:buildingKey', title: 'Skapa underhållsplan' },
    { pattern: '/maintenanceActive/:buildingKey', title: 'Underhållsplan' },
    { pattern: '/report-drafts/:draftKey', title: 'Rapportutkast' },

    // Admin
    { pattern: '/archive', title: 'Ändringslogg' },
    { pattern: '/setup', title: 'Konfigurationsstatus' },
    { pattern: '/superadmin/instance-settings', title: 'Instansinställningar' },
    { pattern: '/superadmin/maintenance-library', title: 'Underhållsbibliotek' },
    { pattern: '/superadmin/lifetime-library', title: 'Livslängdsbibliotek' },
    { pattern: '/superadmin/config-export', title: 'Exportera konfiguration' },
    { pattern: '/superadmin/building-export', title: 'Exportera byggnad' },
    { pattern: '/superadmin/building-import', title: 'Importera byggnad' },
    { pattern: '/superadmin/inventory-times', title: 'Inventeringstider' },
    { pattern: '/superadmin/activity', title: 'Användaraktivitet' },
    { pattern: '/superadmin/data-migrations', title: 'Datamigrering' },

    { pattern: '/versionshistorik', title: 'Versionshistorik' },

    // Parking queue before the Parkering section's own patterns
    { pattern: '/parking-queue', title: 'Parkeringskö' },

    // Hyresadmin
    ...rentalAdminMenuItems.map((item) => ({ pattern: item.path, title: item.label })),

    // Menu-derived names, so the top bar always matches the menu item that led here
    ...settingsMenuItems.map((item) => ({ pattern: menuItemPattern(item.path), title: item.label })),
    { pattern: '/settings/*', title: 'Inställningar' },
    ...profileMenuItems.map((item) => ({ pattern: item.path, title: item.label })),
    ...mainNavDefinitions.flatMap((definition) =>
        definition.activePatterns.map((pattern) => ({ pattern, title: definition.label })),
    ),
];

/** `tenantsLabel` replaces "Hyresgäster" with the instance's word (useTenantTerm). */
export const getRouteTitle = (pathname: string, tenantsLabel?: string): string =>
    (tenantsLabel && matchPath(TENANTS_PATH, pathname) !== null ? tenantsLabel : undefined)
    ?? routeTitles.find(({ pattern }) => matchPath(pattern, pathname) !== null)?.title
    ?? DEFAULT_ROUTE_TITLE;

/** Menu items with Hyresgäster renamed to the instance's word (useTenantTerm). */
export const withTenantsLabel = (items: NavigationMenuItem[], tenantsLabel: string): NavigationMenuItem[] =>
    items.map((item) => (item.path === TENANTS_PATH ? { ...item, label: tenantsLabel } : item));
